import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useOpenBillingPortal } from '@/api/hooks/useBilling'
import { SubscriptionBanner } from '@/components/molecules/subscription-banner'
import { SubscribeDialog } from '@/components/organisms/subscribe-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useIndividuals } from '@/hooks/useIndividuals'
import { useSubscription } from '@/contexts/subscription-context'

// Same four lines shown in the subscribe dialog and the trial/expired banner — reused
// rather than duplicated, so what Funkity includes never drifts out of sync between the
// places it's listed.
const INCLUDED_KEYS = [
  'subscribeDialog.included.documents',
  'subscribeDialog.included.conversations',
  'subscribeDialog.included.findSupport',
  'subscribeDialog.included.sharedAccess',
] as const

/**
 * The subscription section on Settings, and the only place an ACTIVE subscriber can reach
 * their billing.
 *
 * `SubscriptionBanner` covers the trial and expired cases, but it renders nothing once a
 * subscription is active — correctly, since a paid account should not carry a permanent
 * notice. That left a paying customer with no way to cancel, change a card or see what
 * they pay for. This is that way.
 *
 * Which button is shown turns on `hasProviderBilling`, not on the status. After the
 * billing backfill every pre-existing individual is `active` with no Stripe customer
 * behind it, so keying on "active" would offer the portal to people it would 404 for.
 */
export const SubscriptionSettings = () => {
  const { i18n, t } = useTranslation()
  const { selectedIndividual } = useIndividuals()
  const { currentPeriodEnd, hasProviderBilling, isOwner, status } = useSubscription()
  const portal = useOpenBillingPortal()
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false)

  // Trial and expired are exactly the cases the banner on Home/Documents already covers —
  // reusing it here instead of a parallel "no plan yet" layout keeps that copy and design
  // in one place.
  if (status !== 'active') return <SubscriptionBanner />

  const renewal = currentPeriodEnd
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
        new Date(currentPeriodEnd),
      )
    : undefined

  return (
    <div className="card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-heading text-lg font-extrabold">
              {t('subscriptionSettings.title')}
            </h4>
            <Badge className="border-transparent bg-brand-funkia/10 text-brand-funkia" variant="outline">
              {t('subscriptionSettings.status.active')}
            </Badge>
          </div>
          {renewal && (
            <p className="mt-0.5 text-sm text-text-secondary">
              {t('subscriptionSettings.renewsOn', { date: renewal })}
            </p>
          )}
        </div>

        {isOwner &&
          // Manage only when there's an active subscription behind it to manage — the
          // billing backfill left some "active" individuals with no Stripe customer, and
          // those get the same "Skaffa Funkity" CTA as everywhere else rather than a portal
          // button for billing that isn't currently paying for anything.
          (hasProviderBilling ? (
            <Button isLoading={portal.isPending} onClick={() => portal.open()} variant="outline">
              {t('subscriptionSettings.manage')}
            </Button>
          ) : (
            <Button onClick={() => setIsSubscribeOpen(true)}>
              {t('subscriptionSettings.subscribe')}
            </Button>
          ))}
      </div>

      <div>
        <p className="text-sm font-semibold text-brand-gran">
          {t('subscriptionSettings.intro', { name: selectedIndividual?.firstName ?? '' })}
        </p>
        <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
          {INCLUDED_KEYS.map(key => (
            <li className="flex items-start gap-2 text-sm text-text-secondary" key={key}>
              <CheckIcon className="mt-0.5 size-4 shrink-0 text-brand-funkia" strokeWidth={3} />
              {t(key)}
            </li>
          ))}
        </ul>
      </div>

      {!isOwner && (
        <>
          <Separator />
          <p className="text-xs text-text-tertiary">{t('subscriptionSettings.notOwner')}</p>
        </>
      )}

      <SubscribeDialog onOpenChange={setIsSubscribeOpen} open={isSubscribeOpen} />
    </div>
  )
}
